// CircleReveal — exit transition: a circular hole opens in the covering
// layer at a given origin and expands until the cover is gone, revealing
// whatever is rendered underneath. Companion to SweepReveal (entrances);
// this one is for exits. The lock screen uses it after a successful unlock.
//
// Mechanics: the hole is an inline radial-gradient mask whose radius is
// driven by requestAnimationFrame with an ease-out cubic. Deliberately NOT a
// CSS @property/keyframe animation: animating gradient stops needs Houdini
// support that older WKWebViews lack, while an inline style update per frame
// (~27 frames at 450ms) works everywhere and costs nothing.
//
// Interaction: the moment the reveal starts, pointer events are disabled on
// the cover, so the app underneath is tappable immediately; the animation
// never gates input. The layer underneath is expected to be already mounted
// and painted (it is, in LockGate's case), so nothing re-renders as the
// circle passes.
//
// prefers-reduced-motion collapses the reveal to a short opacity fade.

import { useEffect, useRef } from 'react';

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

// playing: false while covering, flip true to run the exit once.
// origin: { x, y } in viewport px, or null for screen center.
// onDone: fires when the cover is fully open — unmount the cover there.
const CircleReveal = ({ playing, origin, duration = 450, onDone, children, style }) => {
  const ref = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!playing) return;
    const el = ref.current;
    if (!el) { doneRef.current?.(); return; }
    el.style.pointerEvents = 'none';
    let raf;
    const start = performance.now();

    if (reducedMotion()) {
      const FADE_MS = 180;
      const step = (t) => {
        const p = Math.min((t - start) / FADE_MS, 1);
        el.style.opacity = String(1 - p);
        if (p < 1) raf = requestAnimationFrame(step);
        else doneRef.current?.();
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    }

    const w = window.innerWidth, h = window.innerHeight;
    const x = origin?.x ?? w / 2;
    const y = origin?.y ?? h / 2;
    // Far enough to clear the farthest corner from the origin.
    const maxR = Math.hypot(Math.max(x, w - x), Math.max(y, h - y));
    // Frame count → onDone, so LOCKSEQ can rule out rAF throttling as a cause of
    // an invisible reveal (a throttled run paints only a handful of frames).
    let frames = 0;
    const step = (t) => {
      frames++;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const r = eased * maxR;
      const mask = `radial-gradient(circle at ${x}px ${y}px, transparent ${r}px, #000 ${r + 0.5}px)`;
      el.style.webkitMaskImage = mask;
      el.style.maskImage = mask;
      if (p < 1) raf = requestAnimationFrame(step);
      else doneRef.current?.({ frames, durationMs: Math.round(t - start) });
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, origin, duration]);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', ...style }}>
      {children}
    </div>
  );
};

export default CircleReveal;
